/* Brain Fuel — article behaviour.
   The contents list is built from the rendered h2s, so it works the same
   whether the body came from stacked blocks or from article.content. */
(function () {
  'use strict';

  function slugify(s) {
    return s.toLowerCase().trim()
      .replace(/[^\w\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .slice(0, 60);
  }

  /* ---- reading progress ------------------------------------------------ */
  function initProgress(root) {
    var fill = root.querySelector('[data-bf-progress-fill]');
    var article = root.querySelector('[data-bf-article]');
    if (!fill || !article) return;

    var ticking = false;
    function update() {
      var box = article.getBoundingClientRect();
      var total = box.height - window.innerHeight;
      var pct = total <= 0 ? (box.bottom <= window.innerHeight ? 1 : 0)
                           : Math.min(Math.max(-box.top / total, 0), 1);
      fill.style.width = (pct * 100).toFixed(2) + '%';
      ticking = false;
    }
    function onScroll() {
      if (!ticking) { ticking = true; window.requestAnimationFrame(update); }
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    update();
  }

  /* ---- contents + scrollspy -------------------------------------------- */
  function initContents(root) {
    var list = root.querySelector('[data-bf-toc]');
    var article = root.querySelector('[data-bf-article]');
    if (!list || !article) return;

    var heads = Array.prototype.slice.call(
      article.querySelectorAll('h2, .bf-h2')
    ).filter(function (h) { return h.textContent.trim().length; });

    if (!heads.length) {
      var wrap = list.closest('[data-bf-toc-wrap]');
      if (wrap) wrap.hidden = true;
      return;
    }

    var links = heads.map(function (h, i) {
      if (!h.id) h.id = slugify(h.textContent) || ('section-' + (i + 1));
      var a = document.createElement('a');
      a.className = 'bf-toc__item';
      a.href = '#' + h.id;
      a.textContent = h.textContent.trim();
      list.appendChild(a);
      return a;
    });

    function setActive(i) {
      links.forEach(function (a, n) { a.classList.toggle('is-active', n === i); });
    }
    setActive(0);

    if (!('IntersectionObserver' in window)) return;

    var seen = new Map();
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) { seen.set(e.target, e.isIntersecting); });
      for (var i = 0; i < heads.length; i++) {
        if (seen.get(heads[i])) { setActive(i); return; }
      }
      // nothing on screen: fall back to the last heading scrolled past
      var last = 0;
      heads.forEach(function (h, i) {
        if (h.getBoundingClientRect().top < window.innerHeight * 0.4) last = i;
      });
      setActive(last);
    }, { rootMargin: '-88px 0px -65% 0px', threshold: 0 });

    heads.forEach(function (h) { io.observe(h); });
  }

  /* ---- share ------------------------------------------------------------ */
  function initShare(root) {
    root.querySelectorAll('[data-bf-share]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var net = btn.getAttribute('data-bf-share');
        var url = encodeURIComponent(window.location.href);
        var title = encodeURIComponent(document.title);
        var map = {
          x: 'https://twitter.com/intent/tweet?url=' + url + '&text=' + title,
          linkedin: 'https://www.linkedin.com/sharing/share-offsite/?url=' + url,
          facebook: 'https://www.facebook.com/sharer/sharer.php?u=' + url
        };
        if (net === 'link') {
          var done = function () {
            var old = btn.textContent;
            btn.textContent = '✓';
            setTimeout(function () { btn.textContent = old; }, 1400);
          };
          if (navigator.clipboard) {
            navigator.clipboard.writeText(window.location.href).then(done, function () {});
          }
          return;
        }
        if (map[net]) window.open(map[net], '_blank', 'noopener,width=600,height=560');
      });
    });
  }

  /* ---- video: swap poster for the real embed on click ------------------- */
  function initVideo(root) {
    root.querySelectorAll('[data-bf-video]').forEach(function (frame) {
      frame.addEventListener('click', function () {
        var src = frame.getAttribute('data-bf-video');
        if (!src) return;
        var iframe = document.createElement('iframe');
        iframe.src = src + (src.indexOf('?') > -1 ? '&' : '?') + 'autoplay=1';
        iframe.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; picture-in-picture';
        iframe.allowFullscreen = true;
        iframe.setAttribute('title', 'Video');
        frame.replaceWith(iframe);
      });
    });
  }

  /* ---- blog index: "Load more" appends instead of navigating ------------ */
  /* Carries the current page's own query params (notably preview_theme_id)
     onto the fetched URL - paginate.next.url is built from request.path
     alone, so on any preview/duplicate theme a plain fetch of it would
     silently hit whichever theme is actually published instead. */
  function withCurrentQueryParams(url) {
    try {
      var target = new URL(url, window.location.href);
      var current = new URL(window.location.href);
      current.searchParams.forEach(function (value, key) {
        if (key !== 'page' && !target.searchParams.has(key)) {
          target.searchParams.set(key, value);
        }
      });
      return target.pathname + target.search;
    } catch (e) {
      return url;
    }
  }

  function initLoadMore(root) {
    var pagination = root.querySelector('.bf-pagination');
    var grid = root.querySelector('.bf-posts__grid');
    if (!pagination || !grid) return;

    var link = pagination.querySelector('a.bf-btn[href]');
    var countEl = pagination.querySelector('.bf-pagination__count');
    var fillEl = pagination.querySelector('.bf-pagination__fill');
    if (!link || link.dataset.bfBound) return;
    link.dataset.bfBound = 'true';

    link.addEventListener('click', function (e) {
      var url = link.getAttribute('href');
      if (!url) return;
      e.preventDefault();

      pagination.setAttribute('aria-busy', 'true');
      link.setAttribute('aria-disabled', 'true');

      fetch(withCurrentQueryParams(url))
        .then(function (res) {
          if (!res.ok) throw new Error('Load more request failed');
          return res.text();
        })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var newGrid = doc.querySelector('.bf-posts__grid');
          var newPagination = doc.querySelector('.bf-pagination');
          var newCards = newGrid ? newGrid.querySelectorAll('.bf-card') : [];

          var firstNew = null;
          newCards.forEach(function (card, i) {
            var imported = document.importNode(card, true);
            grid.appendChild(imported);
            if (i === 0) firstNew = imported;
          });

          var newLink = newPagination ? newPagination.querySelector('a.bf-btn[href]') : null;
          var newCount = newPagination ? newPagination.querySelector('.bf-pagination__count') : null;
          var newFill = newPagination ? newPagination.querySelector('.bf-pagination__fill') : null;

          if (countEl && newCount) countEl.textContent = newCount.textContent;
          if (fillEl && newFill) fillEl.style.width = newFill.style.width;

          if (newLink) {
            link.setAttribute('href', withCurrentQueryParams(newLink.getAttribute('href')));
          } else {
            link.remove();
          }

          pagination.removeAttribute('aria-busy');
          link.removeAttribute('aria-disabled');

          if (firstNew) {
            firstNew.setAttribute('tabindex', '-1');
            firstNew.focus({ preventScroll: true });
          }
        })
        .catch(function () {
          window.location.href = url;
        });
    });
  }

  function init() {
    document.querySelectorAll('[data-bf-root]').forEach(function (root) {
      initProgress(root);
      initContents(root);
      initShare(root);
      initVideo(root);
      initLoadMore(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
})();
